import type { OrgAvailableTypeSchema } from 'types/schema';
/**
 * TreeMapGrid Component
 *
 * Unified grid-based container for TreeMap visualization.
 * Renders all nodes in a single CSS Grid where rows align across all columns.
 */

import React from 'react';
import { useTranslation } from 'localization';
import { Skeleton } from 'primereact/skeleton';
import type { Sphere, TreeMapTemplate, BasicSphereInfo } from './types';
import { expandLevelsWithRepeat } from './types';
import type { TreeMapLayout, GridNode } from './useTreeMapLayout';
import TreeMapGridNode from '../../molecules/TreeMapGridNode/TreeMapGridNode';
import TreeMapChildrenGrid from '../../molecules/TreeMapChildrenGrid/TreeMapChildrenGrid';
import LevelFilterPopoverOrganism from '../LevelFilterPopover/LevelFilterPopoverOrganism';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import { PropertyOption } from '../../molecules/PropertyFilter';
import { PerspectiveOption } from '../../molecules/PerspectiveFilter';
import type { RelationshipOption } from '../../molecules/RelationshipFilter';
import { resolveLabel } from '../../../utils/resolveLabel';
import { resolveLevelConfig, ROOT_COLUMN_LEVEL_ID, clampColumnWidth, getLevelIdForColumn } from './defaults';
import type { BaseViewConfig, TreeMapViewConfig, ComposedLevelFilters } from 'types/contextView';
import type { UnifiedFilters } from 'types/filters';
import type { FilterSaveTarget, FilterSaveTargetAvailability } from '../../../hooks/useLevelFilters';
import type { SchemaForFilters } from '../../../utils/filterPropertyBuilder';
import { hasAnyFilterLane } from '@center-platform/spheres/filters';
import './TreeMapGrid.scss';

export interface TreeMapGridProps {
  layout: TreeMapLayout;
  template: TreeMapTemplate;
  rootTemplateLevel?: number;
  editable?: boolean;
  /** Merged base display config; resolved per-column via `resolveLevelConfig`. */
  baseConfig: BaseViewConfig;
  /** Merged view config; resolved per-column via `resolveLevelConfig`. */
  viewConfig: TreeMapViewConfig;
  /** Initial per-column width (px) from the view's viewConfig.columnWidth. */
  columnWidth?: number;
  paginationState?: Record<string, { nextToken?: string; hasMore: boolean; loading: boolean }>;
  collapsedNodeIds?: Set<string>;
  hiddenColumns?: Set<number>;
  loadingColumns?: Set<number>;
  newSphereIds?: Set<string>;
  onNodeSelect?: (sphereId: string) => void;
  onNodeExpand?: (sphereId: string) => void;
  onNodeCreate?: (sphere: Sphere) => void;
  onNodeUpdate?: (sphere: Sphere) => void;
  onNodeDelete?: (sphereId: string) => void;
  onToggleCollapse?: (nodeId: string) => void;
  onToggleColumnVisibility?: (columnIndex: number) => void;
  /** Fired when a column resize completes, with the resized column's level id and clamped width. */
  onColumnWidthChange?: (levelId: string, width: number) => void;
  onLoadMore?: (sphereId: string, level: number) => Promise<void>;
  onFocus?: (sphereId: string) => void;
  schemas?: any[];
  sphereLookup?: Record<string, BasicSphereInfo>;
  // Per-level runtime filters (EAINT-781) — `LevelFilterPopoverOrganism` per column.
  /** Composed context/view/user/session filters, keyed by `ContextLevel.id`. */
  levelFilters?: Record<string, ComposedLevelFilters>;
  /** Always all three targets, in blast-radius order — from `useLevelFilters`. */
  filterSaveTargets?: FilterSaveTargetAvailability[];
  onSetLevelFilter?: (levelId: string, filters: UnifiedFilters, target: FilterSaveTarget) => void;
  /** Whether an active shared filter link is suppressing each level's own view/user/session layers (EAINT-793) — the popover must not compose a save against a suppressed base. */
  linkSuppressed?: boolean;
  /** Leaves shared-link mode (EAINT-793). Required whenever `linkSuppressed` can be true — a popover save under suppression waits on it. */
  onExitSharedLink?: () => void;
  /** Whether the caller holds `edit` on the CONTEXT — gates the popover's "Edit in context" link. */
  canEditContext?: boolean;
  onEditInContext?: (levelId: string) => void;
  contextSystemName?: string | null;
  /** Display name of the active view — shown in the "no_view_edit_permission" save-target reason. */
  activeViewDisplayName?: string;
  /**
   * Resolver for a column's scope note — e.g. "Applies to repeat 2 of 3 only".
   * Called with the level id; return undefined when the level has repeat === 1.
   */
  getLevelScopeNote?: (levelId: string) => string | undefined;
  // Filter options (will be calculated by parent component)
  getAvailableProperties?: (columnIndex: number) => PropertyOption[];
  /** Unscoped org-wide catalog for the advanced editor's rail (tiered against column scope). */
  orgWideFilterProperties?: PropertyOption[];
  /** Resolves a property key to its display label (e.g. `type` -> "Sphere Type"). */
  getFilterPropertyLabel?: (propertyKey: string) => string;
  /** Resolves a stored filter value to its label (e.g. `center_pillar` -> "Pillar"). */
  getFilterPropertyValueLabel?: (propertyKey: string, value: string) => string;
  /** The column's type scope as system_names -- tiers the advanced editor's rail. */
  getScopeTypesForColumn?: (columnIndex: number) => string[];
  /** Org type catalog the rail sections by. Without it the rail renders one flat list. */
  filterFamilyTypeSchemas?: OrgAvailableTypeSchema[];
  /** True while the org type catalog is still loading -- distinct from "no aspects". */
  filterPropertiesLoading?: boolean;
  /** Non-null when the org type catalog failed to load -- distinct from "no aspects". */
  filterPropertiesError?: string | null;
  getAvailablePerspectives?: (columnIndex: number) => PerspectiveOption[];
  /** The column's relationships — built from the same eligible schemas as its catalog, so the Relationship lane never opens on an empty picker. */
  getAvailableRelationships?: (columnIndex: number) => RelationshipOption[];
  // Props for PerspectiveSearch within filters
  filterPerspectiveSearchProps?: {
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  /** Schemas in scope — forwarded to every column popover's catalog (EAINT-936). */
  allSchemas?: SchemaForFilters[];
  /**
   * Fires when a column popover's "Share with view…" is confirmed for the
   * given level — receives that level's own user-tier clauses (EAINT-936).
   */
  onShareWithView?: (levelId: string, clauses: UnifiedFilters) => void | Promise<void>;
}

const TreeMapGrid: React.FC<TreeMapGridProps> = ({
  layout,
  template,
  rootTemplateLevel = 0,
  editable = false,
  baseConfig,
  viewConfig,
  columnWidth,
  paginationState = {},
  collapsedNodeIds = new Set(),
  hiddenColumns = new Set(),
  loadingColumns = new Set(),
  newSphereIds = new Set(),
  onNodeSelect,
  onNodeExpand,
  onNodeCreate,
  onNodeUpdate,
  onNodeDelete,
  onToggleCollapse,
  onToggleColumnVisibility,
  onColumnWidthChange,
  onLoadMore,
  onFocus,
  schemas,
  sphereLookup,
  levelFilters = {},
  filterSaveTargets = [],
  onSetLevelFilter,
  linkSuppressed = false,
  onExitSharedLink,
  canEditContext = false,
  onEditInContext,
  contextSystemName,
  activeViewDisplayName,
  getLevelScopeNote,
  getAvailableProperties,
  orgWideFilterProperties,
  getFilterPropertyLabel,
  getFilterPropertyValueLabel,
  getScopeTypesForColumn,
  filterFamilyTypeSchemas,
  filterPropertiesLoading,
  filterPropertiesError,
  getAvailablePerspectives,
  getAvailableRelationships,
  filterPerspectiveSearchProps,
  allSchemas,
  onShareWithView,
}) => {
  const { t, i18n } = useTranslation();

  // In-flight column dimming: columns being re-walked after a filter change dim
  // to 40% opacity (dev note). Keyed by levelId; cleared when walk completes.
  const [dimmingColumns, setDimmingColumns] = React.useState<Set<string>>(new Set());
  const debounceTimers = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Wrap onSetLevelFilter to add optimistic dimming + 300ms debounce (dev notes).
  const handleSetLevelFilterWithDim = React.useCallback(
    (levelId: string, filters: UnifiedFilters, target: FilterSaveTarget) => {
      if (!onSetLevelFilter) return;

      // Dim the column optimistically
      setDimmingColumns((prev) => new Set([...prev, levelId]));

      // Debounce: cancel any pending timer for this column
      const existing = debounceTimers.current.get(levelId);
      if (existing) clearTimeout(existing);

      // Fire after 300ms — only once the user stops editing (dev note: debounce)
      const timer = setTimeout(() => {
        debounceTimers.current.delete(levelId);
        onSetLevelFilter(levelId, filters, target);
        // Clear dim after a short delay to let the walk complete
        setTimeout(() => {
          setDimmingColumns((prev) => {
            const next = new Set(prev);
            next.delete(levelId);
            return next;
          });
        }, 500);
      }, 300);

      debounceTimers.current.set(levelId, timer);
    },
    [onSetLevelFilter]
  );

  // Expand template levels to account for repeat property — computed first
  // (before the column-widths state below) since hydration needs to map a
  // column index to the level id that keys `viewConfig.columnState`.
  // Example: [{label: 'Subject', repeat: 1}, {label: 'Sphere', repeat: 3}]
  // Expands to: [{label: 'Subject'}, {label: 'Sphere'}, {label: 'Sphere'}, {label: 'Sphere'}]
  // Memoized so the identity is stable: `resolveLevelIdForColumn` depends on it,
  // and that in turn feeds `handleResizeEnd` and the mousemove/mouseup effect —
  // a fresh array each render would re-attach both listeners on every render of a drag.
  const expandedLevels = React.useMemo(
    () => expandLevelsWithRepeat(template.levels),
    [template.levels]
  );

  /** Resolves a column index to its `columnState` key; `null` when the column maps to no level. */
  const resolveLevelIdForColumn = React.useCallback(
    (column: number): string | null => getLevelIdForColumn(expandedLevels, rootTemplateLevel, column),
    [expandedLevels, rootTemplateLevel]
  );

  // Refs and state for resizable columns
  const gridContainerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);

  const defaultColumnWidth = columnWidth ?? 300;

  // Column resize handling. `resizingColumn` is declared here (ahead of
  // `columnWidths` below) because `columnWidths` gates the in-session buffer
  // by it — see that memo's comment.
  const [resizingColumn, setResizingColumn] = React.useState<number | null>(null);

  // In-session drag buffer, keyed by LEVEL ID — never by column index. Only
  // ever read for the column currently being dragged (see `columnWidths`
  // below); everything else reads the resolved `viewConfig.columnState`
  // directly, which already carries the session-layer write a completed drag
  // produces. Keying by level id (not index) matches the write path
  // (`handleResizeEnd` emits by level id), and drilling down changes
  // `rootTemplateLevel` — an index-keyed map would leave a resized level's
  // width sitting on whatever level slid into that column position.
  const [widthOverridesByLevel, setWidthOverridesByLevel] = React.useState<Record<string, number>>({});

  // Effective width per rendered column: the live drag buffer wins ONLY for
  // the column actively being dragged (so a drag reads smoothly without
  // writing the session layer on every pixel) — every other column, and this
  // one once the drag ends, reads straight from the resolved
  // viewConfig.columnState. This is what makes Reset (which clears the
  // session layer upstream) revert the rendered width immediately rather
  // than being shadowed by a stale local value — the EAINT-775 SC#4 gap.
  // Without an `onColumnWidthChange` consumer there is nowhere to hand the new
  // width off to, so the resolved config never learns about it. In that mode
  // the drag buffer IS the source of truth and is kept past mouseup — otherwise
  // a resize would visibly snap back the instant the mouse is released for
  // read-only embeds and stories, which held the width for the session before
  // board state moved into the resolver.
  const isWidthControlled = !!onColumnWidthChange;

  const columnWidths = React.useMemo<Record<number, number>>(() => {
    const widths: Record<number, number> = {};
    for (let i = 0; i < layout.totalColumns; i++) {
      const levelId = getLevelIdForColumn(expandedLevels, rootTemplateLevel, i);
      const resized =
        levelId && (resizingColumn === i || !isWidthControlled)
          ? widthOverridesByLevel[levelId]
          : undefined;
      const storedWidth = levelId ? viewConfig.columnState?.[levelId]?.width : undefined;
      widths[i] = clampColumnWidth(resized ?? storedWidth ?? defaultColumnWidth);
    }
    return widths;
  }, [
    layout.totalColumns,
    expandedLevels,
    rootTemplateLevel,
    widthOverridesByLevel,
    resizingColumn,
    isWidthControlled,
    viewConfig.columnState,
    defaultColumnWidth,
  ]);

  // ResizeObserver to measure container width
  React.useEffect(() => {
    if (!gridContainerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(gridContainerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Mirrors columnWidths without becoming a handleResizeEnd dependency — otherwise
  // handleResizeEnd's identity (and the mousemove/mouseup listener effect below)
  // would churn on every pixel of drag instead of only at resize start/end.
  const columnWidthsRef = React.useRef(columnWidths);
  React.useEffect(() => {
    columnWidthsRef.current = columnWidths;
  }, [columnWidths]);
  const [resizeStartX, setResizeStartX] = React.useState(0);
  const [resizeStartWidth, setResizeStartWidth] = React.useState(0);

  const handleResizeStart = (columnIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setResizingColumn(columnIndex);
    setResizeStartX(e.clientX);
    setResizeStartWidth(columnWidths[columnIndex] || 300);
  };

  const handleResizeMove = React.useCallback((e: MouseEvent) => {
    if (resizingColumn === null) return;

    const levelId = resolveLevelIdForColumn(resizingColumn);
    if (!levelId) return;

    const delta = e.clientX - resizeStartX;
    const newWidth = Math.max(200, Math.min(600, resizeStartWidth + delta)); // Min 200px, Max 600px

    setWidthOverridesByLevel(prev => ({
      ...prev,
      [levelId]: newWidth,
    }));
  }, [resizingColumn, resizeStartX, resizeStartWidth, resolveLevelIdForColumn]);

  const handleResizeEnd = React.useCallback(() => {
    if (resizingColumn !== null) {
      const levelId = resolveLevelIdForColumn(resizingColumn);
      const clamped = clampColumnWidth(columnWidthsRef.current[resizingColumn] ?? defaultColumnWidth);
      // Only emit when the drag actually changed the width. A mousedown/mouseup
      // on the divider with no movement would otherwise push a no-op patch and
      // light the unsaved-changes indicator for a user who only clicked.
      if (levelId && clamped !== clampColumnWidth(resizeStartWidth)) {
        onColumnWidthChange?.(levelId, clamped);
      }
      // Drop the drag buffer entry now that the column is no longer the
      // active drag — `columnWidths` only reads it while `resizingColumn`
      // matches anyway, but clearing it here keeps the map from accumulating
      // one stale entry per level ever dragged in the session. Skipped when
      // uncontrolled: there the buffer is the only record of the width.
      if (levelId && onColumnWidthChange) {
        setWidthOverridesByLevel((prev) => {
          if (!(levelId in prev)) return prev;
          const next = { ...prev };
          delete next[levelId];
          return next;
        });
      }
    }
    setResizingColumn(null);
  }, [resizingColumn, resizeStartWidth, defaultColumnWidth, resolveLevelIdForColumn, onColumnWidthChange]);

  React.useEffect(() => {
    if (resizingColumn !== null) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);

      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizingColumn, handleResizeMove, handleResizeEnd]);

  // Generate column header labels
  interface ColumnHeader {
    column: number;
    label: string;
    icon?: string;
  }

  // When focused on a sphere at a specific template level, show headers starting from that level
  // rootTemplateLevel 0 = Focus level, so show "Focus" + all expanded template levels
  // rootTemplateLevel 2 = Priority level, so show Priority, Strategy, Intention (based on expanded levels)
  const columnHeaders: ColumnHeader[] = rootTemplateLevel === 0
    ? [
        { column: 0, label: t('treeMap.grid.focus'), icon: undefined },
        ...expandedLevels.map((level, index) => ({
          column: index + 1,
          label: resolveLabel(level.label, i18n.language),
          icon: level.icon,
        })),
      ]
    : expandedLevels
        .slice(rootTemplateLevel - 1) // expandedLevels is 0-indexed, rootTemplateLevel accounts for Focus at 0
        .map((level, index) => ({
          column: index,
          label: resolveLabel(level.label, i18n.language),
          icon: level.icon,
        }));

  // Just offset by 1 for grid positioning
  const columnOffset = 1;

  // Filter nodes based on collapsed state
  // Build set of all descendants of collapsed nodes
  const hiddenNodeIds = React.useMemo(() => {
    const hidden = new Set<string>();

    const addDescendants = (nodeId: string) => {
      const node = layout.nodes.get(nodeId);
      if (!node) return;

      for (const childId of node.children) {
        hidden.add(childId);
        addDescendants(childId); // Recursively hide all descendants
      }
    };

    // For each collapsed node, hide all its descendants
    collapsedNodeIds.forEach((nodeId) => {
      addDescendants(nodeId);
    });

    return hidden;
  }, [layout.nodes, collapsedNodeIds]);

  // Recalculate layout when nodes are collapsed OR columns are hidden - reflow to eliminate gaps.
  // Level filters don't need a trigger here: they're applied server-side (EAINT-781), so a
  // filter change arrives as a new `layout` prop (fresh `spheresByColumn`), not a client-side refilter.
  // GENERIC VERSION: works with any template structure
  const effectiveLayout = React.useMemo(() => {
    // Check if we need to recalculate
    const needsRecalculation = collapsedNodeIds.size > 0 || (hiddenColumns && hiddenColumns.size > 0);

    // If nothing is collapsed and no columns hidden, use original layout
    if (!needsRecalculation) {
      return {
        nodes: Array.from(layout.nodes.values()),
        totalRows: layout.totalRows,
        swimLanes: layout.swimLanes,
      };
    }

    // Filter visible nodes (exclude descendants of collapsed nodes AND nodes in hidden columns AND filtered out nodes)
    const visibleNodes = Array.from(layout.nodes.values()).filter((node) => {
      // Exclude descendants of collapsed nodes
      if (hiddenNodeIds.has(node.sphere.id)) {
        return false;
      }

      // Exclude nodes in hidden columns
      if (hiddenColumns?.has(node.column)) {
        return false;
      }

      // No client-side filtering - all filtering is done on the backend
      // When users apply filters, the parent component reloads data from the backend
      return true;
    });

    // Create a mutable copy of nodes for recalculation
    const recalculatedNodes = visibleNodes.map(node => ({
      ...node,
      // Collapsed nodes get rowSpan=1
      rowSpan: collapsedNodeIds.has(node.sphere.id) ? 1 : node.rowSpan,
    }));

    // Find the last visible column for boundary checking
    const visibleColumns = Array.from({ length: layout.totalColumns }, (_, i) => i)
      .filter(col => !hiddenColumns?.has(col));
    const lastVisibleColumn = visibleColumns[visibleColumns.length - 1];

    // Helper: recursively reflow a node and all its descendants (only in visible columns)
    const reflowNode = (node: typeof recalculatedNodes[0], startRow: number): number => {
      node.rowStart = startRow;
      let currentRow = startRow;

      if (collapsedNodeIds.has(node.sphere.id)) {
        // Collapsed node: just 1 row, children are hidden
        node.rowSpan = 1;
        return startRow + 1;
      }

      // Get children of this node (next column, only if next column is visible)
      const nextColumn = node.column + 1;
      const isNextColumnVisible = !hiddenColumns?.has(nextColumn);

      // If next column is hidden OR we're at the last visible column, node gets 1 row
      if (!isNextColumnVisible || node.column === lastVisibleColumn) {
        node.rowSpan = 1;
        return startRow + 1;
      }

      const children = recalculatedNodes.filter(
        n => n.parentId === node.sphere.id && n.column === nextColumn
      ).sort((a, b) => a.rowStart - b.rowStart); // Keep original order

      if (children.length === 0) {
        // No children: node gets 1 row
        node.rowSpan = 1;
        return startRow + 1;
      }

      // Check if children will be rendered as TreeMapChildrenGrid (flexbox wrapping)
      // This applies when nextColumn is the last visible AND >= 1 (Pillars, Priorities, Strategies, Intentions)
      const childrenUseFlexGrid = nextColumn === lastVisibleColumn && nextColumn >= 1;

      if (childrenUseFlexGrid) {
        // TreeMapChildrenGrid wraps cards horizontally-first (flex-direction: row)
        // Cards fill left-to-right, then wrap to next row
        const CARD_WIDTH = 280; // Card width
        const HORIZONTAL_GAP = 12; // Horizontal gap between cards
        const VERTICAL_GAP = 12; // Vertical gap between card rows
        const CONTAINER_PADDING = 8; // Container padding (top and bottom)
        const FOOTER_HEIGHT = 36; // Footer height when showing Load More or when there are children
        const HIDDEN_INDICATOR_WIDTH = 300; // Hidden indicator column width

        // Card heights by view - must match TreeMapGridNode.tsx ROW_HEIGHT_BY_VIEW
        const CARD_HEIGHT_BY_VIEW: Record<string, number> = {
          minimal: 50,
          standard: 160,
          expanded: 280,
        };
        const nextLevelConfig = nextColumn === 0 ? undefined : expandedLevels?.[rootTemplateLevel + nextColumn - 1];
        const nextColumnDensity = resolveLevelConfig({ base: baseConfig, view: viewConfig }, nextLevelConfig?.id ?? ROOT_COLUMN_LEVEL_ID).cardDensity;
        const cardHeight = CARD_HEIGHT_BY_VIEW[nextColumnDensity] || 160;

        // Calculate available width for this column
        const otherColumnsWidth = visibleColumns
          .filter(col => col !== nextColumn)
          .reduce((sum, col) => sum + (columnWidths[col] || 300), 0);
        const hiddenIndicatorWidth = (hiddenColumns && hiddenColumns.size > 0) ? HIDDEN_INDICATOR_WIDTH : 0;
        const availableWidth = containerWidth - otherColumnsWidth - hiddenIndicatorWidth;

        // Calculate how many card columns can fit
        const maxCardColumns = Math.floor(
          (availableWidth - 2 * CONTAINER_PADDING + HORIZONTAL_GAP) / (CARD_WIDTH + HORIZONTAL_GAP)
        );
        const cardsPerRow = Math.max(1, maxCardColumns);

        // Calculate number of rows of cards needed
        // Include add card in count if editable
        const totalCards = children.length + (editable && onNodeCreate ? 1 : 0);
        const rowsOfCards = Math.ceil(totalCards / cardsPerRow);

        // Check if this parent has more children to load (footer shown if hasMore)
        const parentHasMore = paginationState[node.sphere.id]?.hasMore || false;

        // Calculate actual pixel height needed for TreeMapChildrenGrid content
        // Height = border + padding + (rows * cardHeight) + ((rows-1) * verticalGap) + padding + border + footer
        // Footer is only shown when children.length > 0 AND hasMore
        const BORDER_WIDTH = 1; // Container border
        const showsFooter = children.length > 0 && parentHasMore;
        const contentHeight =
          2 * BORDER_WIDTH + // top + bottom border
          2 * CONTAINER_PADDING + // top + bottom padding
          rowsOfCards * cardHeight + // card rows
          Math.max(0, rowsOfCards - 1) * VERTICAL_GAP + // gaps between rows
          (showsFooter ? FOOTER_HEIGHT : 0); // footer if showing Load More

        // Calculate how many grid rows are needed
        // Each grid row has height = cardHeight (from sibling column cards)
        // So rowSpan = ceil(contentHeight / cardHeight)
        const rowsNeeded = Math.ceil(contentHeight / cardHeight);

        // Minimum 1 row
        node.rowSpan = Math.max(1, rowsNeeded);

        return startRow + node.rowSpan;
      }

      // Process children recursively (individual cards, 1 row each)
      const childStartRow = currentRow;
      for (const child of children) {
        currentRow = reflowNode(child, currentRow);
      }

      // Check if this node will show a footer in the next column
      const nodeHasMore = paginationState[node.sphere.id]?.hasMore || false;
      const nodeShowsFooter = children.length > 0 && (nodeHasMore || (editable && !!onNodeCreate));

      // Store footer flag on node for use during rendering
      node.__showsFooter = nodeShowsFooter;

      // Add extra row for footer if needed
      if (nodeShowsFooter) {
        currentRow += 1;
      }

      // Node spans all its children plus footer row if present
      node.rowSpan = currentRow - childStartRow;
      return currentRow;
    };

    // Start reflow from swim lanes (column 1 nodes)
    let currentRow = 1;

    for (const swimLane of layout.swimLanes) {
      const swimLaneNode = recalculatedNodes.find(n => n.sphere.id === swimLane.id);
      if (!swimLaneNode) continue;

      currentRow = reflowNode(swimLaneNode, currentRow);
    }

    // Update root node (column 0) to span all swim lanes
    const rootNode = recalculatedNodes.find(n => n.column === 0);
    if (rootNode) {
      rootNode.rowStart = 1;
      rootNode.rowSpan = currentRow - 1;
    }

    const newTotalRows = currentRow - 1;

    // Recalculate swim lane rowSpans based on new layout
    const recalculatedSwimLanes = layout.swimLanes.map(swimLane => {
      const swimLaneNode = recalculatedNodes.find(n => n.sphere.id === swimLane.id);
      if (!swimLaneNode) return swimLane;

      return {
        ...swimLane,
        rowStart: swimLaneNode.rowStart,
        rowSpan: swimLaneNode.rowSpan,
      };
    });


    return {
      nodes: recalculatedNodes,
      totalRows: newTotalRows,
      swimLanes: recalculatedSwimLanes,
    };
  }, [layout, collapsedNodeIds, hiddenNodeIds, hiddenColumns, containerWidth, columnWidths, baseConfig, viewConfig]);



  // Calculate dynamic column widths based on content
  const gridTemplateColumns = React.useMemo(() => {
    // Find visible columns and last visible column
    const visibleColumns = Array.from({ length: layout.totalColumns }, (_, i) => i)
      .filter(col => !hiddenColumns?.has(col));
    const lastVisibleColumn = visibleColumns[visibleColumns.length - 1];

    // Calculate width for last visible column based on available space
    let lastColumnWidth = columnWidths[lastVisibleColumn] || 300;

    if (lastVisibleColumn !== undefined && lastVisibleColumn >= 1 && containerWidth > 0) {
      // Calculate space used by all other visible columns
      const otherColumnsWidth = visibleColumns
        .filter(col => col !== lastVisibleColumn)
        .reduce((sum, col) => sum + (columnWidths[col] || 300), 0);

      // Available space for child grid column
      // Subtract hidden indicator width to prevent overflow
      const HIDDEN_INDICATOR_WIDTH = 300; // Same as default column width
      const hiddenIndicatorWidth = (hiddenColumns && hiddenColumns.size > 0) ? HIDDEN_INDICATOR_WIDTH : 0;
      const availableWidth = containerWidth - otherColumnsWidth - hiddenIndicatorWidth;

      // Get all parents from the previous column
      const parentColumn = lastVisibleColumn - 1;
      const parents = effectiveLayout.nodes.filter(n => n.column === parentColumn);

      // Count children for each parent in the last visible column
      const childrenCounts = parents.map(parent => {
        const children = effectiveLayout.nodes.filter(
          n => n.column === lastVisibleColumn && n.parentId === parent.sphere.id
        );
        // Include add card in count if editable
        return children.length + (editable && onNodeCreate ? 1 : 0);
      });

      // Get max children count
      const maxChildren = childrenCounts.length > 0 ? Math.max(...childrenCounts) : 0;

      if (maxChildren > 0) {
        // Calculate max columns that can fit based on available space
        const CARD_WIDTH = 280;
        const GAP = 12;
        const GRID_PADDING = 16;
        const BORDER_WIDTH = 2;

        // How many card columns can fit?
        const maxCardColumns = Math.floor(
          (availableWidth - GRID_PADDING - BORDER_WIDTH + GAP) / (CARD_WIDTH + GAP)
        );

        // Use the lesser of maxChildren and what fits in available space
        const columnsNeeded = Math.min(maxChildren, Math.max(maxCardColumns, 1));

        const cardsTotalWidth = CARD_WIDTH * columnsNeeded;
        const gapsTotalWidth = GAP * (columnsNeeded - 1);
        lastColumnWidth = cardsTotalWidth + gapsTotalWidth + GRID_PADDING + BORDER_WIDTH;

      }
    }

    // Build column widths array ONLY for visible columns
    const columnsArray = visibleColumns.map(col => {
      if (col === lastVisibleColumn && lastVisibleColumn >= 1) {
        return `${lastColumnWidth}px`;
      }
      return `${columnWidths[col] || 300}px`;
    });

    // Add explicit width for hidden indicator column if present
    // The hidden indicator is positioned after all visible columns
    if (hiddenColumns && hiddenColumns.size > 0) {
      columnsArray.push('300px'); // Width for hidden indicator column (same as default column width)
    }

    return columnsArray.join(' ');
  }, [layout.totalColumns, effectiveLayout.nodes, hiddenColumns, columnWidths, containerWidth]);

  return (
    <div ref={gridContainerRef} className="tree-map-grid-container">
      {/* Column Headers */}
      <div
        className="tree-map-grid-header"
        style={{
          gridTemplateColumns,
        }}
      >
        {columnHeaders
          .filter((header) => !hiddenColumns?.has(header.column))
          .map((header) => (
            <div
              key={header.column}
              className={`tree-map-grid-header__column${
                (() => { const lid = resolveLevelIdForColumn(header.column); return lid && dimmingColumns.has(lid) ? ' tree-map-grid-header__column--dimming' : ''; })()
              }`}
              style={{
                gridColumn: header.column + columnOffset,
              }}
            >
              {header.icon && <GlyphAtom glyph={header.icon} />}
              <span>{header.label}</span>
              {/* Column Filter popover - only for columns 1+ (not Focus column) */}
              {header.column > 0 && onSetLevelFilter && getAvailableProperties && (() => {
                const levelId = resolveLevelIdForColumn(header.column);
                if (!levelId) return null;
                return (
                  <LevelFilterPopoverOrganism
                    scopeId={levelId}
                    composed={levelFilters[levelId] ?? { runtime: {}, display: {}, provenance: [] }}
                    canEditContext={canEditContext}
                    onEditInContext={onEditInContext ? () => onEditInContext(levelId) : undefined}
                    saveTargets={filterSaveTargets}
                    onSave={(filters, target) => handleSetLevelFilterWithDim(levelId, filters, target)}
                    linkSuppressed={linkSuppressed}
                    onExitSharedLink={onExitSharedLink}
                    contextSystemName={contextSystemName}
                    availableProperties={getAvailableProperties(header.column)}
                    orgWideProperties={orgWideFilterProperties}
                    {...(getFilterPropertyLabel ? { getPropertyLabel: getFilterPropertyLabel } : {})}
                    {...(getFilterPropertyValueLabel
                      ? { getPropertyValueLabel: getFilterPropertyValueLabel }
                      : {})}
                    scopeTypes={getScopeTypesForColumn?.(header.column) ?? []}
                    familyTypeSchemas={filterFamilyTypeSchemas ?? []}
                    propertiesLoading={filterPropertiesLoading}
                    propertiesError={filterPropertiesError}
                    availablePerspectives={getAvailablePerspectives?.(header.column) || []}
                    availableRelationships={getAvailableRelationships?.(header.column) ?? []}
                    perspectiveSearchProps={{
                      schemaSystemName: '',
                      sphereId: layout.nodes.get(Array.from(layout.nodes.keys())[0])?.sphere?.id || '',
                      direction: filterPerspectiveSearchProps?.direction,
                      hops: filterPerspectiveSearchProps?.hops,
                    }}
                    triggerLabel={header.label}
                    scopeNote={getLevelScopeNote?.(levelId)}
                    activeViewDisplayName={activeViewDisplayName}
                    allSchemas={allSchemas}
                    onShareWithView={
                      onShareWithView ? (clauses) => onShareWithView(levelId, clauses) : undefined
                    }
                  />
                );
              })()}
              {/* Hide/Show column button - only for columns 1+ (not Focus column) */}
              {header.column > 0 && (
                <button
                  className="tree-map-grid-header__hide-btn"
                  onClick={() => onToggleColumnVisibility?.(header.column)}
                  title={
                    hiddenColumns?.has(header.column + 1)
                      ? t('treeMap.grid.showHiddenColumns')
                      : t('treeMap.grid.hideColumnsRight')
                  }
                >
                  <i className={`pi ${hiddenColumns?.has(header.column + 1) ? "pi-eye" : "pi-eye-slash"}`} />
                </button>
              )}
              {/* Resize handle - don't show on last visible column */}
              {header.column < layout.totalColumns - 1 && !hiddenColumns?.has(header.column + 1) && (
                <span
                  className="tree-map-grid-header__resize-handle"
                  onMouseDown={(e) => handleResizeStart(header.column, e)}
                  aria-hidden="true"
                />
              )}
            </div>
          ))}
        {/* Hidden Columns Indicator */}
        {hiddenColumns && hiddenColumns.size > 0 && (
          <div
            className="tree-map-grid-header__hidden-indicator"
            style={{
              gridColumn: columnHeaders.filter(h => !hiddenColumns.has(h.column)).length + columnOffset,
            }}
          >
            <button
              className="tree-map-grid-header__unhide-btn"
              onClick={() => {
                // Click the first hidden column to unhide all
                const firstHidden = Array.from(hiddenColumns).sort((a, b) => a - b)[0];
                onToggleColumnVisibility?.(firstHidden);
              }}
              title={t('treeMap.grid.showHiddenColumns')}
            >
              <i className="pi pi-eye" />
              <span>{t('treeMap.grid.hiddenCount', { count: hiddenColumns.size })}</span>
            </button>
          </div>
        )}
      </div>

      {/* Grid Content Area with Spacer */}
      <div style={{ display: 'flex', flex: 1, overflow: 'auto' }}>
        {/* Main Grid */}
        <div
          className="tree-map-grid"
          style={{
            gridTemplateColumns,
            gridTemplateRows: `repeat(${effectiveLayout.totalRows}, auto)`,
          }}
        >
        {/* Swim Lane Backgrounds */}
        {effectiveLayout.swimLanes.map((swimLane, index) => {
          // Exclude footer row from background if swim lane has a footer
          const swimLaneShowsFooter = swimLane.__showsFooter || false;
          const backgroundRowSpan = swimLaneShowsFooter ? Math.max(1, swimLane.rowSpan - 1) : swimLane.rowSpan;

          return (
            <div
              key={`swimlane-bg-${swimLane.id}`}
              className="tree-map-grid__swimlane-bg"
              style={{
                gridColumn: `1 / span ${layout.totalColumns}`,
                gridRow: `${swimLane.rowStart} / span ${backgroundRowSpan}`,
                backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
              }}
            />
          );
        })}

        {/* GENERIC COLUMN RENDERING: Works with any template */}
        {(() => {
          // Calculate which columns are visible and which is the last visible
          const visibleColumns = Array.from({ length: layout.totalColumns }, (_, i) => i)
            .filter(col => !hiddenColumns?.has(col));
          const lastVisibleColumn = visibleColumns[visibleColumns.length - 1];

          return Array.from({ length: layout.totalColumns }, (_, i) => i).flatMap((columnIndex) => {
            const column = columnIndex;

            // Skip hidden columns
            if (hiddenColumns?.has(column)) {
              return null;
            }

            const isLastColumn = column === lastVisibleColumn; // Use last VISIBLE column

            // Get level config for this column (column 0 is root, doesn't have level config)
            // When focused, offset by rootTemplateLevel to get the correct level from the EXPANDED template
            // Example: focused on Priority (rootTemplateLevel=2), column 1 should use expandedLevels[2] (Strategy)
            const levelConfig = column === 0
              ? undefined
              : expandedLevels?.[rootTemplateLevel + column - 1];

            // This column's effective density (level override → view/base config → 'standard').
            // Repeated levels (repeat > 1) share one override — they resolve through the same levelConfig.id.
            const columnCardView = resolveLevelConfig({ base: baseConfig, view: viewConfig }, levelConfig?.id ?? ROOT_COLUMN_LEVEL_ID).cardDensity;

            const nodesInColumn = effectiveLayout.nodes.filter(n => n.column === column);

            // --- LOADING STATE: Show skeleton cards ---
            if (loadingColumns.has(column)) {
              // Render skeleton cards for loading column
              const skeletonCards = Array.from({ length: 3 }, (_, idx) => (
                <div
                  key={`skeleton-${column}-${idx}`}
                  className="tree-map-grid-node tree-map-grid-node--skeleton"
                  style={{
                    gridColumn: column + 1 + columnOffset,
                    gridRow: idx + 2, // Start from row 2 (after header)
                  }}
                >
                  <div className="tree-map-grid-node__content">
                    <Skeleton width="100%" height="24px" className="mb-2" />
                    <Skeleton width="75%" height="16px" className="mb-1" />
                    <Skeleton width="60%" height="16px" />
                  </div>
                </div>
              ));

              return skeletonCards;
            }

            // --- LAST VISIBLE COLUMN: TreeMapChildrenGrid for flexbox wrapping ---
            // Only apply flex grid to columns 1+ (Pillars, Priorities, Strategies, Intentions)
            // Column 0 (Focus) should always render as individual card
            if (isLastColumn && column >= 1) {
            // Get all parents from previous column
            const parentColumn = column - 1;
            const allParents = effectiveLayout.nodes.filter(n => n.column === parentColumn);

            // Group children by parent
            const nodesByParent = new Map<string, typeof nodesInColumn>();
            for (const node of nodesInColumn) {
              const parentId = node.parentId;
              if (!parentId) continue;

              if (!nodesByParent.has(parentId)) {
                nodesByParent.set(parentId, []);
              }
              nodesByParent.get(parentId)!.push(node);
            }

            // Render ONE children grid per parent
            return allParents.map((parentNode) => {
              const children = nodesByParent.get(parentNode.sphere.id) || [];
              const hasMore = paginationState[parentNode.sphere.id]?.hasMore || false;
              const isParentCollapsed = collapsedNodeIds.has(parentNode.sphere.id);

              // Don't render if parent is collapsed
              if (isParentCollapsed) {
                return null;
              }

              // If parent has a footer row, exclude it from children grid span
              const parentShowsFooter = parentNode.__showsFooter || false;
              const childrenGridRowSpan = parentShowsFooter ? Math.max(1, parentNode.rowSpan - 1) : parentNode.rowSpan;

              return (
                <div
                  key={`${parentNode.sphere.id}-children-grid`}
                  style={{
                    gridColumn: column + columnOffset,
                    gridRow: `${parentNode.rowStart} / span ${childrenGridRowSpan}`,
                    // No height constraints - content determines swim lane height
                  }}
                >
                  <TreeMapChildrenGrid
                    parentNode={parentNode}
                    childNodes={children}
                    levelConfig={levelConfig}
                    template={template}
                    editable={editable}
                    baseConfig={baseConfig}
                    viewConfig={viewConfig}
                    hasMore={hasMore}
                    isLoadingMore={paginationState[parentNode.sphere.id]?.loading || false}
                    collapsedNodeIds={collapsedNodeIds}
                    paginationState={paginationState}
                    newSphereIds={newSphereIds}
                    onNodeSelect={onNodeSelect}
                    onNodeExpand={onNodeExpand}
                    onNodeCreate={onNodeCreate}
                    onNodeUpdate={onNodeUpdate}
                    onNodeDelete={onNodeDelete}
                    onToggleCollapse={onToggleCollapse}
                    onLoadMore={() => onLoadMore?.(parentNode.sphere.id, parentNode.column)}
                    onFocus={onFocus}
                    schemas={schemas}
                    sphereLookup={sphereLookup}
                  />
                </div>
              );
            });
          }

          // --- COLUMNS 1+ (except last column): Individual cards with Load More footer ---
          // Apply footer overlay to columns 1+ (Pillars, Priorities, Strategies) that are NOT the last column
          // Last column >= 1 uses TreeMapChildrenGrid which has its own built-in footer
          // Only Column 0 (Focus) doesn't get a footer
          if (column >= 1 && !isLastColumn) {
            // Get all parents from previous column
            const parentColumn = column - 1;
            const allParents = effectiveLayout.nodes.filter(n => n.column === parentColumn);

            // Group nodes by parent
            const nodesByParent = new Map<string, GridNode[]>();
            nodesInColumn.forEach(node => {
              const parentId = node.parentId;
              if (!parentId) return;

              if (!nodesByParent.has(parentId)) {
                nodesByParent.set(parentId, []);
              }
              nodesByParent.get(parentId)!.push(node);
            });

            // Render each child node individually positioned in its own grid row + footer after last child
            return allParents.flatMap((parentNode) => {
              const childrenInColumn = (nodesByParent.get(parentNode.sphere.id) || [])
                .sort((a, b) => a.rowStart - b.rowStart); // Ensure children are in row order
              const isParentCollapsed = collapsedNodeIds.has(parentNode.sphere.id);

              // If parent is collapsed, don't render anything
              if (isParentCollapsed) {
                return [];
              }

              const hasMore = paginationState[parentNode.sphere.id]?.hasMore || false;
              const isLoadingMore = paginationState[parentNode.sphere.id]?.loading || false;

              // Zero-rows + active filter → "No match" state (dev note: zero rows needs an out).
              // Shown when the column is filtered and the filter eliminates all children.
              const columnLevelId = resolveLevelIdForColumn(column);
              if (childrenInColumn.length === 0 && columnLevelId) {
                const composed = levelFilters?.[columnLevelId];
                const hasActiveFilter = composed && hasAnyFilterLane(composed.display);
                if (hasActiveFilter && onSetLevelFilter) {
                  const sessionPropertyFilters = composed.display.propertyFilters ?? [];
                  return (
                    <div
                      key={`${parentNode.sphere.id}-no-match`}
                      className="tree-map-grid-node tree-map-grid-no-match"
                      style={{
                        gridColumn: column + columnOffset,
                        gridRow: `${parentNode.rowStart} / span ${parentNode.rowSpan}`,
                      }}
                    >
                      <span className="tree-map-grid-no-match__text">
                        {t('treeMap.grid.noMatch', { column: columnHeaders.find(h => h.column === column)?.label ?? '' })}
                      </span>
                      {sessionPropertyFilters.length > 0 && (
                        <button
                          type="button"
                          className="tree-map-grid-no-match__relax-btn"
                          onClick={() => {
                            const next = sessionPropertyFilters.slice(0, -1);
                            onSetLevelFilter(columnLevelId, { ...composed.display, propertyFilters: next }, 'session');
                          }}
                        >
                          {t('treeMap.grid.relaxLastFilter')}
                        </button>
                      )}
                    </div>
                  );
                }
              }

              // If there are NO children and we're editable, render a container with background and centered add card
              if (childrenInColumn.length === 0 && editable && onNodeCreate) {
                // Height should match actual card heights for each detail level
                const ADD_CARD_HEIGHT: Record<string, number> = {
                  minimal: 50,
                  standard: 160,
                  expanded: 280,
                };
                const addCardHeight = ADD_CARD_HEIGHT[columnCardView] || 120;

                return (
                  <div
                    key={`${parentNode.sphere.id}-empty-container`}
                    style={{
                      gridColumn: column + columnOffset,
                      gridRow: `${parentNode.rowStart} / span ${parentNode.rowSpan}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px',
                      background: 'var(--component-background)',
                      borderLeft: '1px solid rgba(0, 0, 0, 0.05)',
                      borderRight: '1px solid rgba(0, 0, 0, 0.05)',
                      borderBottom: '3px solid rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    <button
                      type="button"
                      className={`tree-map-children-grid__add-card tree-map-children-grid__add-card--${columnCardView}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onNodeCreate({ ...parentNode.sphere, __columnIndex: parentNode.column });
                      }}
                      style={{
                        width: '280px',
                        height: `${addCardHeight}px`,
                        minHeight: `${addCardHeight}px`,
                      }}
                    >
                      <div className="tree-map-children-grid__add-card-content">
                        <i className="pi pi-plus" />
                        <span>{t('treeMap.grid.addItem', { label: resolveLabel(levelConfig?.label, i18n.language) || t('common.item') })}</span>
                      </div>
                    </button>
                  </div>
                );
              }

              // Render each child as individual grid-positioned card
              const childCards = childrenInColumn.map((node, nodeIdx) => {
                const isCollapsed = collapsedNodeIds.has(node.sphere.id);
                const hasChildren = node.children.length > 0;

                return (
                  <TreeMapGridNode
                    // A sphere can legitimately appear more than once in a column
                    // (child of multiple parents). Key by parent + sphere + index so
                    // siblings flattened across parents never collide.
                    key={`${parentNode.sphere.id}-${node.sphere.id}-${nodeIdx}`}
                    node={node}
                    levelConfig={levelConfig}
                    template={template}
                    editable={editable}
                    baseConfig={baseConfig}
                    viewConfig={viewConfig}
                    columnOffset={columnOffset}
                    hasMore={false}
                    isLoadingMore={false}
                    isCollapsed={isCollapsed}
                    hasChildren={hasChildren}
                    isNew={newSphereIds.has(node.sphere.id)}
                    isLastColumn={isLastColumn}
                    onSelect={() => onNodeSelect?.(node.sphere.id)}
                    onExpand={() => onNodeExpand?.(node.sphere.id)}
                    onCreate={() => onNodeCreate?.(node.sphere)}
                    onUpdate={() => onNodeUpdate?.(node.sphere)}
                    onDelete={() => onNodeDelete?.(node.sphere.id)}
                    onToggleCollapse={() => onToggleCollapse?.(node.sphere.id)}
                    onFocus={() => onFocus?.(node.sphere.id)}
                    schemas={schemas}
                    sphereLookup={sphereLookup}
                  />
                );
              });

              // Render footer in its own dedicated row (last row of parent's span)
              const footerRow = parentNode.rowStart + parentNode.rowSpan - 1;
              const footer = (childrenInColumn.length > 0 && hasMore) ? (
                <div
                  key={`${parentNode.sphere.id}-footer`}
                  style={{
                    gridColumn: column + columnOffset,
                    gridRow: footerRow, // Position in dedicated footer row
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 2px 3px 2px', // Account for swim lane borders
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '16px',
                      padding: '8px 14px',
                      background: 'var(--component-background)',
                      borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                      minHeight: '36px',
                      width: '100%',
                    }}
                  >
                    {hasMore && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onLoadMore?.(parentNode.sphere.id, parentNode.column);
                        }}
                        disabled={isLoadingMore}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: isLoadingMore ? '#999' : '#377fc4',
                          cursor: isLoadingMore ? 'wait' : 'pointer',
                          fontSize: '13px',
                          padding: '0',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <i className={`pi ${isLoadingMore ? 'pi-spinner pi-spin' : 'pi-angle-down'}`} style={{ fontSize: '12px' }} />
                        <span>{isLoadingMore ? t('common.loading') : t('common.loadMore')}</span>
                      </button>
                    )}
                  </div>
                </div>
              ) : null;

              return footer ? [...childCards, footer] : childCards;
            });
          }

          // --- REGULAR COLUMNS (Column 0: Focus): Individual TreeMapGridNode cards ---
          // Only Column 0 reaches here - it renders as individual card without footer
          // All other columns are handled by the conditions above
          return nodesInColumn.map((node, nodeIdx) => {
            const isCollapsed = collapsedNodeIds.has(node.sphere.id);
            const hasChildren = node.children.length > 0;
            const hasMore = paginationState[node.sphere.id]?.hasMore || false;
            const isLoadingMore = paginationState[node.sphere.id]?.loading || false;

            // If node is collapsed, use rowSpan=1 instead of calculated rowSpan
            const adjustedNode = isCollapsed && hasChildren
              ? { ...node, rowSpan: 1 }
              : node;

            return (
              <TreeMapGridNode
                // Disambiguate by index — the same sphere may appear multiple times.
                key={`${node.sphere.id}-${nodeIdx}`}
                node={adjustedNode}
                levelConfig={levelConfig}
                template={template}
                editable={editable}
                baseConfig={baseConfig}
                viewConfig={viewConfig}
                columnOffset={columnOffset}
                hasMore={column === 0 ? false : hasMore}
                isLoadingMore={column === 0 ? false : isLoadingMore}
                isCollapsed={isCollapsed}
                hasChildren={hasChildren}
                isLastColumn={isLastColumn}
                onSelect={() => onNodeSelect?.(node.sphere.id)}
                onToggleCollapse={() => onToggleCollapse?.(node.sphere.id)}
                onLoadMore={column === 0 ? undefined : () => onLoadMore?.(node.sphere.id, node.column)}
                onFocus={() => onFocus?.(node.sphere.id)}
                onUpdate={() => onNodeUpdate?.(node.sphere)}
                schemas={schemas}
                sphereLookup={sphereLookup}
              />
            );
          });
          });
        })()}
        </div>

        {/* Spacer to fill remaining width with subtle background */}
        <div
          style={{
            flex: 1,
            background: 'var(--component-background, #f8f9fa)',
            minWidth: 0,
          }}
        />
      </div>
    </div>
  );
};

export default TreeMapGrid;
